import React, { useState, useEffect } from "react";
import { Typography } from "@mui/material";
import { Check, CircleX, Loader2, X } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { LinkAccountParams, User } from "@lib/types/users";
import { linkAccount } from "@lib/api/mutations/auth";
import { AxiosError } from "axios";
import { useAuth } from "@context/AuthContext";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import useHandleError from "@lib/utils/errorHook";

const WialonLinkAccount: React.FC = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();

  const { invitation_code } = useParams();
  const [error, setError] = useState<string>("");

  const queryParams = new URLSearchParams(window.location.search);
  const token = queryParams.get("access_token");

  useEffect(() => {
    if (token && invitation_code) {
      handleLinkAccount(token, invitation_code);
    } else {
      setError(t("user.linkAccount.invalidToken"));
    }
  }, [invitation_code, t, token]);

  const { mutate: linkAccountMutation, isPending: isLinkingLoading } =
    useMutation({
      mutationFn: (data: LinkAccountParams) => linkAccount(data),
      onSuccess: (data: User) => {
        if (data) {
          setTimeout(() => {
            window.location.href = "/login";
          }, 10000);
        }
      },
      onError: (error: AxiosError | unknown) => {
        if (error instanceof AxiosError && error.response) {
          const errorDetails = handleError(error.response.data);
          errorDetails && setError(errorDetails.message);
        } else {
          setError(t("general.error"));
        }
      },
    });

  const handleLinkAccount = (token: string, invitation_code: string) => {
    linkAccountMutation({ token, invitation_code });
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-sm p-8 bg-white rounded-lg shadow-lg">
        {isLinkingLoading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="animate-spin w-10 h-10 text-blue-500 mb-4" />
            <Typography variant="h6" className="text-gray-800">
              {t("user.linkAccount.loading")}
            </Typography>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center">
            <CircleX className="w-10 h-10 text-red-500 mb-4" />

            <Typography variant="body1" className="text-red-500 text-center">
              {error}
            </Typography>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <Check className="w-10 h-10 text-green-500 mb-4" />
            <Typography variant="h6" className="text-gray-800">
              {t("user.linkAccount.success")}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default WialonLinkAccount;
